import ReassessmentAdaptation from "./ReassessmentAdaptation";
import {preferenceShares, changePreferenceShare} from "../helpers/reassessmentPreferences";
import ReassessmentPreferences from "./ReassessmentPreferences";
import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  leagueTeamIds,
  leagueProposals,
  leaguePayload,
  normalizedLeagueRows,
  assertLeagueBalance,
} from "../helpers/leagueReassessment";
import {
  reviewProfiles,
  profileWeights,
  settingDirection,
} from "../helpers/teamReassessment";
import {
  futureSummary,
} from "../helpers/teamAnalysis";
import { goalFieldLabel } from "../helpers/teamSettingValues";
import { referenceAudit } from "./TeamModelReferences";
import TeamSettingsImpact from "./TeamSettingsImpact";
import LeagueStatisticAudit from "./LeagueStatisticAudit";
import LeagueConversionOverview from "./LeagueConversionOverview";
import LeagueModelComparison from "./LeagueModelComparison";

export default function LeagueReassessment({
  data,
  league,
  seasonId,
  request,
  en,
  mode,
  onSaved,
}) {
  const { i18n } = useTranslation(),
    t = (d, e) => (en ? e : d);
  const [open, setOpen] = useState(false),
    [step, setStep] = useState(0),
    [profile, setProfile] = useState("balanced");
  const [weights, setWeights] = useState(() =>
    profileWeights("balanced", {}, () => 0.5)
  );
  const [pace, setPace] = useState(25),
    [statPace, setStatPace] = useState(25),
    [recent, setRecent] = useState(8);
  const [preserve, setPreserve] = useState({}),
    [rows, setRows] = useState(null),
    [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [effects, setEffects] = useState(null);
  const [saved, setSaved] = useState(false),
    [partial, setPartial] = useState(false);
  const version = useRef(0),
    snapshot = useRef(null),
    generation = useRef(0);
  const ids = leagueTeamIds(data),
    fmt = (v, d = 2) =>
      Number.isFinite(v)
        ? v.toLocaleString(en ? "en-GB" : "de-DE", {
            minimumFractionDigits: d,
            maximumFractionDigits: d,
            useGrouping: false,
          })
        : "—";
  const names = (id) =>
    data.result_teams?.[id]?.team_name ||
    data.actual_table?.find((r) => Number(r.team_id) === Number(id))
      ?.team_name ||
    data.observations?.find((f) => Number(f.home_team_id) === Number(id))
      ?.home_name ||
    data.observations?.find((f) => Number(f.away_team_id) === Number(id))
      ?.away_name ||
    String(id);
  const logo = (id) =>
    data.team_images?.[id] ? (
      <img
        src={data.team_images[id]}
        alt=""
        style={{
          width: 28,
          height: 28,
          objectFit: "contain",
          verticalAlign: "middle",
          marginRight: 8,
        }}
      />
    ) : null;
  const statName = (key) =>
    key === "conversion"
      ? "Conversion"
      : i18n.t(
          "help.automation.business.statisticNames." +
            data.state?.registry?.[key]?.title,
          { defaultValue: data.state?.registry?.[key]?.title || key }
        );
  const labels = {
    should: t("Saisonleistung", "Season performance"),
    real: t("Ergebnisse", "Results"),
    market: t("Markt", "Market"),
    recent: t("Jüngste Leistung", "Recent performance"),
    other: t("Statistikmodell", "Statistics model"),
    previous: t("Vorsaison", "Previous season"),
    external: "xGoals",
  };
  const call = (mode, body = {}) =>
    request("statistic-model", {
      league_id: Number(league.id),
      season_id: Number(seasonId),
      admin_team_analysis: true,
      team_ids: ids,
      mode,
      ...body,
    });
  const invalidate = () => {
    setPreview(null);
    setEffects(null);
  };
  const valid = rows?.every((team) =>
    [...team.result, ...team.statistics].every(
      (r) =>
        Number.isFinite(r.value) &&
        r.value >= (r.group === "statistic" ? 0 : Number.EPSILON) &&
        (String(r.statistic_id) !== "45" || r.value <= 1)
    )
  );
  async function check(nextRows, drawOnly = false) {
    const token = ++generation.current;
    setBusy(true);
    setError("");
    try {
      const payload = leaguePayload(snapshot.current, nextRows);
      if (payload.edits.length > 2500)
        throw Error(
          t(
            "Zu viele Einzelwerte für eine gemeinsame Vorschau. Nichts gespeichert.",
            "Too many values for one preview. Nothing saved."
          )
        );
      const sp = payload.edits.length
        ? await call("team-settings-preview", {
            edits: payload.edits,
            expected_revision: snapshot.current.state.revision,
          })
        : { state: snapshot.current.state, ranking: snapshot.current.ranking };
      const rp = await call("team-result-preview", {
        strengths: payload.strengths,
        expected_revision: snapshot.current.result_revision,
        statistic_edits: payload.edits,
        statistic_revision: snapshot.current.state?.revision,
        draw_factor_impact: drawOnly,
      });
      if (token !== generation.current) return;
      payload.draft = JSON.parse(rp.teamsRaw);
      assertLeagueBalance(payload.draft, sp.state, ids);
      version.current++;
      setRows(normalizedLeagueRows(nextRows, payload.draft, sp.state));
      setPreview({
        payload,
        state: sp.state,
        ranking: sp.ranking || [],
        parameters: JSON.parse(rp.parametersRaw),
        probabilities: rp.probability_impact,
      });
      if (drawOnly) setEffects(rp.draw_factor_impact || {});
    } catch (e) {
      setPreview(null);
      setEffects(null);
      setError(e.message);
    } finally {
      if (token === generation.current) setBusy(false);
    }
  }
  function generate() {
    setNotice("");
    try {
      snapshot.current = data;
      version.current++;
      const next = leagueProposals(
        data,
        ids,
        weights,
        pace,
        statPace,
        recent,
        preserve
      );
      setRows(next);
      setStep(1);
      invalidate();
      check(next);
    } catch (e) {
      setError(e.message);
    }
  }
  function edit(id, group, index, text) {
    setRows((current) =>
      current.map((team) =>
        team.id !== id
          ? team
          : {
              ...team,
              [group]: team[group].map((r, i) =>
                i !== index
                  ? r
                  : {
                      ...r,
                      manual: true,
                      rawProposal: undefined,
                      value:
                        text === ""
                          ? null
                          : Number(text.replace(",", ".")) /
                            (r.group === "conversion" ||
                            String(r.statistic_id) === "45"
                              ? 100
                              : 1),
                    }
              ),
            }
      )
    );
    invalidate();
  }
  const change = (field, before, after, group = "") => {
    const d = settingDirection(field, before, after, group);
    return (
      <span className={d > 0 ? "tw-positive" : d < 0 ? "tw-negative" : ""}>
        {Number.isFinite(after) && Number.isFinite(before)
          ? `${after > before ? "+" : ""}${fmt(after - before)}`
          : "—"}
      </span>
    );
  };
  const pair = (field, a, b) => (
    <>
      {fmt(a)} → <strong>{fmt(b)}</strong>
      <br />
      {change(field, a, b)}
    </>
  );
  const explainTeam = (id) => {
    const element = document.getElementById(`league-review-team-${id}`);
    if (element) { element.open = true; element.scrollIntoView({block: "start", behavior: "smooth"}); }
  };
  async function save() {
    if (!preview || partial || saved) return;
    setBusy(true);
    setError("");
    let resultSaved = false;
    try {
      // Revisions are checked again by each existing store. Never silently retry a partial save.
      await call("team-result-save", {
        strengths: preview.payload.strengths,
        expected_revision: snapshot.current.result_revision,
      });
      resultSaved = true;
      if (preview.payload.edits.length)
        await call("team-settings-save", {
          edits: preview.payload.edits,
          expected_revision: snapshot.current.state.revision,
        });
      const confirmed = await call("league-workspace");
      assertLeagueBalance(confirmed.result_teams, confirmed.state, ids);
      const same = (a, b) =>
        Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < 1e-10;
      for (const team of rows) {
        for (const r of team.result)
          if (!same(confirmed.result_teams?.[team.id]?.[r.field], r.value))
            throw Error(
              t(
                "Readback des Ergebnismodells weicht ab.",
                "Results model readback differs."
              )
            );
        for (const r of team.statistics) {
          const actual =
            r.group === "conversion"
              ? confirmed.state?.teams?.[team.id]?.conversion?.[r.field]
              : confirmed.state?.teams?.[team.id]?.statistics?.[
                  r.statistic_id
                ]?.[r.field];
          if (!same(actual, r.value))
            throw Error(
              t(
                "Readback des Statistikmodells weicht ab.",
                "Statistics model readback differs."
              )
            );
        }
      }
      setSaved(true);
      setNotice(
        t(
          "Beide Modelle gespeichert und erneut gelesen. Die neuen Settings gelten für neue Berechnungen. Für die Wochenendspiele: Zu den Spielen → gewünschte Spiele auswählen → im Kompilationsmodus neu berechnen. Bereits berechnete Prognosen werden durch das Speichern allein nicht ersetzt.",
          "Both models saved and read back. The new settings apply to new calculations. For weekend matches: open Matches, select the fixtures and recalculate in compilation mode. Saving alone does not replace previously calculated predictions."
        )
      );
      onSaved?.();
    } catch (e) {
      setPartial(resultSaved);
      if (resultSaved) setStep(1);
      setError(
        (resultSaved
          ? t(
              "Speicherung nur teilweise bestätigt. Ergebnismodell wurde gespeichert. Vor weiteren Änderungen neu laden und prüfen. ",
              "Save only partially confirmed. Results model was saved. Reload and review before further changes. "
            )
          : "") + e.message
      );
    } finally {
      setBusy(false);
    }
  }
  function close() {
    if (busy) return;
    generation.current++;
    setOpen(false);
    if (partial) onSaved?.();
  }
  function requestClose() {
    if (busy) return;
    if (rows && !saved && !partial && !window.confirm(t(
      "Ungespeicherte Vorschläge verwerfen und schließen? Abbrechen führt zurück zur Bearbeitung.",
      "Discard unsaved proposals and close? Cancel returns to editing."
    ))) return;
    close();
  }
  function start() {
    setOpen(true);
    setStep(0);
    setRows(null);
    setPreview(null);
    setEffects(null);
    setError("");
    setNotice("");
    setSaved(false);
    setPartial(false);
    setPreserve(
      Object.fromEntries(
        ids.map((id) => [
          id,
          data.state?.teams?.[id]?.conversion_season_policy?.preserve === true,
        ])
      )
    );
    try {
      const p = JSON.parse(
        localStorage.getItem("betty.team-analysis.reassessment.v1")
      );
      if (p?.weights) {
        setWeights(p.weights);
        setPace(p.pace ?? 25);
        setStatPace(p.statPace ?? 25);
                if (p.recent === 6 || p.recent === 8) setRecent(p.recent);
        setProfile(p.profile || "balanced");
      }
    } catch {}
  }
  const fieldLabel = (k) =>
    ({
      stored_goal_average: t("Liga-Torniveau", "League goal level"),
      exp_home_factor: t("Liga-Heimfaktor", "League home factor"),
      drawfactor: t("Liga-Remisfaktor", "League draw factor"),
    }[k] || goalFieldLabel(k, en));
  return (
    <>
      <Button
        variant="contained"
        disabled={data.read_only || !ids.length}
        onClick={start}
      >
        {t(
          "Liga neu bewerten · beide Modelle",
          "Reassess league · both models"
        )}
      </Button>
      <Dialog
        open={open}
        fullScreen
        onClose={requestClose}
        PaperProps={{ className: `team-analysis tw-league-review ta-${mode}` }}
        sx={{ zIndex: 1800 }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexShrink: 0 }}>
          <span>{league.name} · {data.season?.name || seasonId} ·{" "}
          {t("Liga neu bewerten", "Reassess league")}</span>
          {rows && step > 0 && <Button onClick={()=>document.getElementById("league-conversion-overview")?.scrollIntoView({block:"start",behavior:"smooth"})}>{t("Alle Conversion-Raten", "All conversion rates")}</Button>}
          <Button variant="outlined" disabled={busy} onClick={requestClose} sx={{ flexShrink: 0 }}>
            {t("Schließen", "Close")} <span aria-hidden="true" style={{ marginLeft: 8 }}>×</span>
          </Button>
        </DialogTitle>
        <DialogContent>
          <style>{`.tw-league-review .lr-saved { background: rgba(70,170,220,.18); border-left: 4px solid #65caff; font-weight: 900; } .tw-league-review input:not([type=checkbox]):not([type=range]) { color:#12313c; background:#b8eef4; border:1px solid #65caff; border-radius:4px; padding:6px; }`}</style>
          <p>
            {t(
              "1. Gemeinsame Vorgaben → 2. Alle Teams prüfen → 3. Speichern. Erst ausdrückliches Speichern aktiviert die Änderungen.",
              "1. Shared preferences → 2. Review all teams → 3. Save. Changes only become active when explicitly saved."
            )}
          </p>
          {step === 0 && (
            <section>
              <details open><summary>{t("Persönliche Vorgaben · Fragen beantworten oder ändern", "Personal preferences · answer or change questions")}</summary>
              <ReassessmentPreferences en={en} onApply={p => {setWeights(p.weights); setPace(p.pace); setStatPace(p.statPace); setProfile("balanced"); setRecent(p.recent); setNotice(t("Persönliche Vorgaben übernommen. Vorschläge jetzt neu berechnen.", "Preferences applied. Recalculate proposals now.")); setRows(null); setPreview(null);}} />
            </details>
            <h3>
                {t(
                  "Welche Quellen sollen besonders zählen?",
                  "Which sources should carry more weight?"
                )}
              </h3>
              <p>
                {t(
                  "Die vorgeschlagene ausgewogene Auswahl kann unverändert bleiben. Saison und jüngste Leistung dürfen gemeinsam zählen. Fehlende Quellen erhalten kein wirksames Gewicht.",
                  "You may keep the balanced suggestions. Season and recent performance may both contribute. Missing sources receive no effective weight."
                )}
              </p>
              <label>
                {t("Beratungsprofil", "Adviser profile")}{" "}
                <select
                  value={profile}
                  onChange={(e) => {
                    setProfile(e.target.value);
                    setWeights(profileWeights(e.target.value));
                    setPace(reviewProfiles[e.target.value].pace);
                    setStatPace(reviewProfiles[e.target.value].pace);
                  }}
                >
                  {Object.entries(reviewProfiles).map(([key, p]) => (
                    <option key={key} value={key}>
                      {en ? p.en : p.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="tw-league-preferences">
                {Object.entries(labels).map(([key, label]) => (
                  <label key={key}>
                    {label}{" "}
                    <input
                      aria-label={label}
                      type="range"
                      min="0"
                      max="100"
                      value={preferenceShares(weights)[key] || 0}
                      onChange={(e) =>
                        setWeights(changePreferenceShare(weights, key, e.target.value))
                      }
                    />{" "}
                    {fmt(preferenceShares(weights)[key] || 0, 2)} %
                  </label>
                ))}
              </div>
              <p><strong>{t("Summe Quellenanteile", "Total source shares")}: {Object.values(weights).some(v => v > 0) ? "100" : "0"} %</strong> · {t("Anzeige gerundet. Verfügbare Quellen werden je Team neu normiert.", "Rounded display. Available sources are normalized for each team.")}</p>
              <p>
                {t(
                  "Relative Prioritäten: für jedes Team werden die verfügbaren Quellen auf 100 % verteilt. Markt betont die Markteinschätzung, Saisonleistung die gemessene Leistung, Statistikmodell die unabhängige gespeicherte Einschätzung.",
                  "Relative priorities: available sources are normalized to 100% for each team. Market emphasizes market assessment; season performance emphasizes measured performance; statistics model emphasizes its independent saved assessment."
                )}
              </p>
              <ReassessmentAdaptation en={en} pace={pace} statPace={statPace} />
              <label>
                {t("Jüngste Spiele", "Recent matches")}{" "}
                <select
                  value={recent}
                  onChange={(e) => setRecent(Number(e.target.value))}
                >
                  <option>6</option>
                  <option>8</option>
                </select>
              </label>
              <h3>
                {t(
                  "Welche Teams behalten ihre Conversion-Eigenheit?",
                  "Which teams retain their conversion characteristics?"
                )}
              </h3>
              <p>
                {t(
                  "Angehakte Teams behalten bei dieser Neubewertung ihren gespeicherten Conversion-Abstand zum Ligamittel vollständig, die anderen zu 25 %. Die belegte Saisonleistung wird weiterhin mit n/(n+40) berücksichtigt. Anschließend wird ligaweit normiert; die Vorschau zeigt diesen endgültigen Stand. Die Auswahl gilt außerdem für die vorhandene Saisonwechsel-Regel.",
                  "Selected teams fully retain their saved conversion difference from the league mean in this review; other teams retain 25%. Covered season performance remains weighted by n/(n+40). League normalization follows; the preview shows the final values. The selection also applies to the existing season-transition policy."
                )}
              </p>
              <div className="tw-league-preferences">
                {ids.map((id) => (
                  <label key={id}>
                    <input
                      type="checkbox"
                      checked={!!preserve[id]}
                      onChange={(e) =>
                        setPreserve({ ...preserve, [id]: e.target.checked })
                      }
                    />
                    {logo(id)}
                    {names(id)}
                  </label>
                ))}
              </div>
              <Button
                onClick={() => {
                  try {
                    localStorage.setItem(
                      "betty.team-analysis.reassessment.v1",
                      JSON.stringify({ weights, pace, statPace, profile, recent })
                    );
                    setNotice(
                      t(
                        "Vorgaben für weitere Teams und Ligen auf diesem Gerät gespeichert.",
                        "Preferences saved on this device for other teams and leagues."
                      )
                    );
                  } catch {
                    setError(
                      t(
                        "Profil konnte nicht gespeichert werden.",
                        "Could not save profile."
                      )
                    );
                  }
                }}
              >
                {t(
                  "Vorgaben auf diesem Gerät speichern",
                  "Save preferences on this device"
                )}
              </Button>
              <Button
                variant="contained"
                disabled={busy || !Object.values(weights).some((v) => v > 0)}
                onClick={generate}
              >
                {t(
                  "Vorschläge für alle Teams berechnen",
                  "Calculate proposals for all teams"
                )}
              </Button>
            </section>
          )}
          {rows && step > 0 && (
            <>
              <Alert severity="info">
                {t(
                  "Alle Zahlen: gespeichert → Vorschlag. Jeder Einzelwert bleibt bearbeitbar. Vorschläge sind ligaweit normiert: Heim-Offensive = Auswärts-Defensive, Auswärts-Offensive = Heim-Defensive. Alle Einzelwerte und Saisonparameter zeigen diesen normierten Stand.",
                  "All values: saved → proposed. Every individual value remains editable. Proposals are normalized across the league: home attack = away defence, away attack = home defence. All individual values and season parameters show this normalized state."
                )}
              </Alert>
              {!preview && (
                <Alert severity="warning">
                  {t(
                    "Auswirkungen noch nicht bestätigt. Nach Eingaben Vorschau aktualisieren.",
                    "Effects not yet confirmed. Refresh preview after edits."
                  )}
                </Alert>
              )}
              <LeagueModelComparison rows={rows} saved={snapshot.current} preview={preview} names={names} logo={logo} en={en} onExplain={explainTeam} onDraw={() => check(rows, true)} busy={busy} effects={effects} />
              <LeagueConversionOverview rows={rows} saved={snapshot.current.state} preview={preview?.state} names={names} logo={logo} en={en} onExplain={explainTeam} />
              {preview && (
                <section>
                  <h3>
                    {t(
                      "Season Parameters · gespeichert → Vorschlag",
                      "Season parameters · saved → proposed"
                    )}
                  </h3>
                  <div className="ta-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>{t("Ergebnismodell", "Results model")}</th>
                          <th className="lr-saved">
                            {t(
                              "Gespeichert · zu ändern",
                              "Saved · editing baseline"
                            )}
                          </th>
                          <th>{t("Vorschlag", "Proposed")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          "stored_goal_average",
                          "exp_home_factor",
                          "drawfactor",
                        ].map((key) => (
                          <tr key={key}>
                            <th>{fieldLabel(key)}</th>
                            <td>
                              {fmt(snapshot.current.result_parameters?.[key])}
                            </td>
                            <td>{fmt(preview.parameters?.[key])}</td>
                          </tr>
                        ))}
                        <tr>
                          <th>
                            {t(
                              "Heimvorteil · Heim / Auswärts",
                              "Home advantage · home / away"
                            )}
                          </th>
                          <td>
                            {fmt(
                              snapshot.current.result_parameters
                                ?.exp_home_factor /
                                (2 -
                                  snapshot.current.result_parameters
                                    ?.exp_home_factor)
                            )}
                          </td>
                          <td>
                            {fmt(
                              preview.parameters?.exp_home_factor /
                                (2 - preview.parameters?.exp_home_factor)
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <details>
                    <summary>
                      {t(
                        "Statistikmodell · alle Saisonparameter vorher/nachher",
                        "Statistics model · all season parameters before/after"
                      )}
                    </summary>
                    <div className="ta-scroll">
                      <table>
                        <thead>
                          <tr>
                            <th>{t("Statistik", "Statistic")}</th>
                            {[
                              "home_for",
                              "home_against",
                              "away_for",
                              "away_against",
                            ].map((k) => (
                              <th key={k}>{fieldLabel(k)}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {referenceAudit(preview.state).map((r) => (
                            <tr key={r.key}>
                              <th>{statName(r.key)}</th>
                              {r.values.map((v, i) => {
                                const scale =
                                    r.key === "conversion" || r.key === "45"
                                      ? 100
                                      : 1,
                                  old = referenceAudit(
                                    snapshot.current.state
                                  ).find((x) => x.key === r.key)?.values[i]
                                    ?.stored;
                                return (
                                  <td key={i}>
                                    {fmt(
                                      Number.isFinite(old) ? old * scale : null
                                    )}{" "}
                                    →{" "}
                                    <strong>
                                      {fmt(
                                        Number.isFinite(v.stored)
                                          ? v.stored * scale
                                          : null
                                      )}
                                    </strong>
                                    {scale === 100 ? " %" : ""}
                                    {!v.matches ? " ⚠" : ""}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </section>
              )}
              <details>
                <summary>
                  {t(
                    "Liga-Statistiken prüfen · alle Teams, Attack Moments und Conversion",
                    "Review league statistics · all teams, attack moments and conversion"
                  )}
                </summary>
                <LeagueStatisticAudit
                  data={snapshot.current}
                  proposed={preview?.state}
                  {...{ ids, names, logo, statName, en }}
                />
              </details>
              {rows.map((team) => (
                <details key={team.id} id={`league-review-team-${team.id}`}>
                  <summary>
                    {logo(team.id)}
                    {names(team.id)} ·{" "}
                    {t(
                      "Einzelwerte prüfen und bearbeiten",
                      "Review and edit individual values"
                    )}
                  </summary>
                  {!team.policy && (
                    <Alert severity="warning">
                      {t(
                        "Conversion: ursprüngliche Saisonbasis fehlt. Die Neubewertung verwendet vorhandene gespeicherte Werte und belegte Spiele.",
                        "Conversion: original season baseline missing. Reassessment uses available saved values and recorded matches."
                      )}
                    </Alert>
                  )}
                  <div className="ta-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>{t("Zusammensetzung", "Composition")}</th>
                          <th>{t("Ergebnismodell", "Results model")}</th>
                          <th>{t("Statistikmodell", "Statistics model")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          "for_total",
                          "against_total",
                          "for_home",
                          "against_home",
                          "for_away",
                          "against_away",
                        ].map((k) => (
                          <tr key={k}>
                            <th>{fieldLabel(k)}</th>
                            <td>
                              {pair(
                                k,
                                futureSummary(
                                  snapshot.current.result_teams[team.id]
                                )?.[k],
                                futureSummary(
                                  preview?.payload.draft[team.id]
                                )?.[k]
                              )}
                            </td>
                            <td>
                              {pair(
                                k,
                                futureSummary(
                                  snapshot.current.ranking?.find(
                                    (r) => Number(r.team_id) === team.id
                                  )
                                )?.[k],
                                futureSummary(
                                  preview?.ranking.find(
                                    (r) => Number(r.team_id) === team.id
                                  )
                                )?.[k]
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {["result", "statistics"].map((group) => (
                    <section key={group}>
                      <h3>
                        {group === "result"
                          ? t("Ergebnismodell", "Results model")
                          : t("Statistikmodell", "Statistics model")}
                      </h3>
                      <div className="ta-scroll">
                        <table>
                          <thead>
                            <tr>
                              <th>{t("Wert", "Value")}</th>
                              <th className="lr-saved">
                                {t(
                                  "Gespeichert · zu ändern",
                                  "Saved · editing baseline"
                                )}
                              </th>
                              <th>{t("Erwartet", "Expected")}</th>
                              <th>{t("Erspielt", "Observed")}</th>
                              <th>{t("Vorsaison", "Prior season")}</th>
                              <th>
                                {t(
                                  "Vorschlag · bearbeitbar",
                                  "Proposal · editable"
                                )}
                              </th>
                              <th>Δ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {team[group].map((r, index) => {
                              const scale =
                                r.group === "conversion" ||
                                String(r.statistic_id) === "45"
                                  ? 100
                                  : 1;
                              return (
                                <tr key={r.field + index}>
                                  <th>
                                    {group === "statistics"
                                      ? statName(r.statistic_id) + " · "
                                      : ""}
                                    {fieldLabel(r.field)}
                                    {scale === 100 ? " (%)" : ""}
                                    {r.draw && (
                                      <small style={{ display: "block" }}>
                                        {t(
                                          "Remisen real / erwartet",
                                          "Draws actual / expected"
                                        )}
                                        : {fmt(r.draw.actual, 0)} /{" "}
                                        {fmt(r.draw.expected, 2)} ·{" "}
                                        {r.draw.count} {t("Spiele", "matches")}
                                      </small>
                                    )}
                                  </th>
                                  <td className="lr-saved">
                                    <strong>
                                      {fmt(r.before * scale)}
                                      {scale === 100 ? " %" : ""}
                                    </strong>
                                  </td>
                                  <td>
                                    {Number.isFinite(r.range?.expected)
                                      ? fmt(r.range.expected * scale) +
                                        (scale === 100 ? " %" : "")
                                      : "—"}
                                  </td>
                                  <td>
                                    {Number.isFinite(r.range?.observed)
                                      ? fmt(r.range.observed * scale) +
                                        (scale === 100 ? " %" : "")
                                      : "—"}
                                  </td>
                                  <td>
                                    {Number.isFinite(r.previous)
                                      ? fmt(r.previous * scale) +
                                        (scale === 100 ? " %" : "")
                                      : "—"}
                                  </td>
                                  <td>
                                    <input
                                      aria-label={`${names(team.id)} ${group} ${
                                        r.statistic_id || ""
                                      } ${r.field}`}
                                      key={version.current}
                                      defaultValue={fmt(r.value * scale)}
                                      inputMode="decimal"
                                      disabled={busy || saved || partial}
                                      onChange={(e) =>
                                        edit(
                                          team.id,
                                          group,
                                          index,
                                          e.target.value
                                        )
                                      }
                                    />
                                    {scale === 100 ? " %" : ""}
                                    {r.group === "conversion" && (
                                      <details>
                                        <summary>
                                          {t(
                                            "Rechenweg prüfen",
                                            "Inspect calculation"
                                          )}
                                        </summary>
                                        <p>
                                          {t("Gespeichert", "Saved")}:{" "}
                                          {fmt(r.before * 100)} %;{" "}
                                          {t(
                                            "Saisonursprung",
                                            "Season original"
                                          )}
                                          : {fmt(r.original * 100)} %;{" "}
                                          {t(
                                            "Liga-Saisonbasis",
                                            "League season baseline"
                                          )}
                                          : {fmt(r.standard * 100)} %
                                        </p>
                                        <p>
                                          {r.preserve
                                            ? t(
                                                "Spitzenteam: aktueller Speicherstand bleibt Ausgangsbasis.",
                                                "Elite team: current saved value is the baseline."
                                              )
                                            : t(
                                                "Neutralisierung nur beim ersten Übernehmen; anschließend aktueller Speicherstand.",
                                                "Neutralization only on first adoption; current saved value thereafter."
                                              )}
                                        </p>
                                        <p>
                                          {t("Ausgangsbasis", "Baseline")}:{" "}
                                          {fmt(r.baseline * 100)} %; n ={" "}
                                          {r.range?.count};{" "}
                                          {t("Dämpfung", "Damping")}: n / (n +
                                          100); {t("Grenze", "Limit")}: ±0,24{" "}
                                          {t(
                                            "Prozentpunkte",
                                            "percentage points"
                                          )}
                                          ;{" "}
                                          {t("Anpassungsstärke", "Adaptation")}:{" "}
                                          {fmt(r.progression * 100)} %
                                        </p>
                                        <p>
                                          {r.manual &&
                                            t(
                                              "Manuell bearbeitet. ",
                                              "Manually edited. "
                                            )}
                                          {t(
                                            "Individueller Vorschlag vor Normierung",
                                            "Individual proposal before normalization"
                                          )}
                                          : {fmt(r.rawProposal * 100)} % →{" "}
                                          {t(
                                            "nach Normierung",
                                            "after normalization"
                                          )}
                                          : {fmt(r.value * 100)} %
                                        </p>
                                        <p>
                                          {t(
                                            "Formel: Basis + Anpassungsstärke × begrenzte((erspielt − Basis) × n / (n + 100)). Danach Liga-Abgleich innerhalb des Wegs zwischen gespeichertem Wert und individuellem Vorschlag; keine Richtungsumkehr.",
                                            "Formula: baseline + adaptation × capped((observed − baseline) × n / (n + 100)). Then league balancing within the interval between saved value and individual proposal; no direction reversal."
                                          )}
                                        </p>
                                      </details>
                                    )}
                                    {r.group === "conversion" &&
                                      (r.rawProposal - r.before) *
                                        (r.value - r.before) <
                                        -1e-14 && (
                                        <strong
                                          style={{
                                            display: "block",
                                            color: "#e9a64a",
                                          }}
                                        >
                                          {t(
                                            "Normierung kehrt die individuelle Änderungsrichtung um – Rechenweg prüfen.",
                                            "Normalization reverses the individual direction — inspect calculation."
                                          )}
                                        </strong>
                                      )}
                                  </td>
                                  <td>
                                    {change(
                                      r.field,
                                      r.before * scale,
                                      r.value === null ? null : r.value * scale,
                                      r.group === "conversion"
                                        ? "conversion"
                                        : r.statistic_id
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  ))}
                </details>
              ))}
              <h3>
                {t(
                  "Remisfaktor · Wirkung einschätzen",
                  "Draw factor · estimate effect"
                )}
              </h3>
              <p>
                {t(
                  "Mehr Remisen als erwartet: Faktor sinkt. Weniger: Faktor steigt. Kleine Stichproben werden stark gedämpft. Der Faktor ist ein gemeinsamer Wahrscheinlichkeitsparameter; kein unabhängiger zweiter Teamfaktor im Statistikmodell.",
                  "More draws than expected: lower factor. Fewer: higher factor. Small samples are strongly damped. The factor is a shared probability parameter, not an independent second team factor in the statistics model."
                )}
              </p>
              <Button
                disabled={busy || !valid || saved || partial}
                onClick={() => check(rows, true)}
              >
                {t(
                  "Remiswahrscheinlichkeitsänderung schätzen",
                  "Estimate draw probability change"
                )}
              </Button>
              {effects && (
                <div className="ta-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>{t("Team", "Team")}</th>
                        <th>{t("Vergleichsgegner", "Comparison opponent")}</th>
                        <th>
                          {t(
                            "Änderung · Prozentpunkte",
                            "Change · percentage points"
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(({ id }) => (
                        <tr key={id}>
                          <th>{names(id)}</th>
                          <td>
                            {effects[id] ? names(effects[id].opponent_id) : "—"}
                          </td>
                          <td>
                            {effects[id] && !effects[id].unavailable
                              ? `${
                                  effects[id].delta_percentage_points > 0
                                    ? "+"
                                    : ""
                                }${fmt(effects[id].delta_percentage_points, 2)}`
                              : t("Nicht berechenbar", "Unavailable")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p>
                    {t(
                      "Illustration gegen einen Ranking-Nachbarn: Mittel aus Heim- und Auswärtsspiel. Nur der eigene Remisfaktor ändert sich; Gegner, Torwerte und Liga-Parameter bleiben konstant. Keine exakte Saisonprognose.",
                      "Illustration against a ranking neighbour: average of home and away matches. Only this team’s draw factor changes; opponent, goals and league parameters stay fixed. Not an exact season forecast."
                    )}
                  </p>
                </div>
              )}
              {preview && (
                <details>
                  <summary>
                    {t(
                      "Season Parameters und sämtliche Auswirkungen · vorher/nachher",
                      "Season parameters and all effects · before/after"
                    )}
                  </summary>
                  <TeamSettingsImpact
                    data={snapshot.current}
                    draft={preview.payload.draft}
                    stat={preview.state}
                    ranking={preview.ranking}
                    parameters={preview.parameters}
                    probabilities={preview.probabilities}
                    ids={ids}
                    {...{ en, names, logo, fieldLabel }}
                  />
                </details>
              )}
            </>
          )}
          {busy && (
            <p role="status">{t("Berechnung läuft …", "Calculating …")}</p>
          )}
          {notice && <Alert severity="success">{notice}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          {step > 0 && !saved && <span className="lmc-save-status">{t("Vorschau – noch nicht gespeichert", "Preview – not saved yet")}</span>}
          <Button disabled={busy} onClick={close}>
            {partial
              ? t("Schließen und neu laden", "Close and reload")
              : saved
              ? t("Schließen", "Close")
              : t("Verwerfen und schließen", "Discard and close")}
          </Button>
          {step > 0 && !saved && !partial && (
            <>
              <Button
                disabled={busy}
                onClick={() => {
                  setStep(0);
                  setRows(null);
                  invalidate();
                }}
              >
                {t("Vorgaben ändern", "Change preferences")}
              </Button>
              <Button disabled={busy || !valid} onClick={() => check(rows)}>
                {t("Vorschau aktualisieren", "Refresh preview")}
              </Button>
              <Button
                variant="contained"
                disabled={busy || !preview || !valid}
                onClick={() => setStep(2)}
              >
                {t(
                  "Änderungen zur Speicherung prüfen",
                  "Review changes for saving"
                )}
              </Button>
            </>
          )}
        </DialogActions>
        <Dialog
          open={step === 2 && !saved && !partial}
          onClose={() => {
            if (!busy) setStep(1);
          }}
          sx={{ zIndex: 1900 }}
        >
          <DialogTitle>
            {t(
              "Beide Modelle dieser Liga speichern?",
              "Save both models of this league?"
            )}
          </DialogTitle>
          <DialogContent>
            {t(
              `${
                rows?.length || 0
              } Teams einschließlich der neu berechneten Saisonparameter. Erst jetzt werden die persönlichen Settings geändert. Die beiden Modelle werden nacheinander gespeichert; ein Teilfehler wird ausdrücklich gemeldet.`,
              `${
                rows?.length || 0
              } teams including recalculated season parameters. This changes your personal settings. Models are saved sequentially; partial failures are explicitly reported.`
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={() => setStep(1)}>
              {t("Zur Prüfung zurück", "Back to review")}
            </Button>
            <Button disabled={busy || !preview} onClick={save}>
              {t("Jetzt beide Modelle speichern", "Save both models now")}
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
    </>
  );
}
